import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import PropTypes from "prop-types";

/**
 * Reusable DateField wrapper around MUI DatePicker.
 * Supports string (YYYY-MM-DD) or moment value. Emits string in same format via synthetic event (target.name/value)
 * to stay compatible with existing handleChange(event) forms.
 */
const DateField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  required = false,
  error = false,
  helperText = "",
  minDate,
  maxDate,
  disableFuture = false,
  disablePast = false,
  shouldDisableDate,
  fullWidth = true,
  format = "YYYY-MM-DD",
  includeProvider = true,
  size,
  ...pickerProps
}) => {
  let parsed = null;
  if (value) {
    parsed = moment.isMoment(value) ? value : moment(value);
  }

  const handleDateChange = (newVal) => {
    const str = newVal ? newVal.format(format) : "";
    if (onChange) {
      // mimic native event shape
      onChange({ target: { name, value: str } });
    }
  };

  const picker = (
    <DatePicker
      label={label}
      value={parsed}
      onChange={handleDateChange}
      disableFuture={disableFuture}
      disablePast={disablePast}
      shouldDisableDate={shouldDisableDate}
      minDate={minDate ? moment(minDate) : undefined}
      maxDate={maxDate ? moment(maxDate) : undefined}
      slotProps={{
        textField: {
          id: name,
          name,
          required,
          size,
          fullWidth,
          error: Boolean(error),
          helperText,
          onBlur,
          FormHelperTextProps: {
            sx: { color: error ? "error.main" : "inherit" },
          },
        },
      }}
      {...pickerProps}
    />
  );

  if (!includeProvider) return picker;
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {picker}
    </LocalizationProvider>
  );
};

DateField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // moment or string
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  helperText: PropTypes.node,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  shouldDisableDate: PropTypes.func,
  fullWidth: PropTypes.bool,
  format: PropTypes.string,
  includeProvider: PropTypes.bool,
};

export default DateField;
